import { ERR_INVALID_ARGUMENT } from "../error";
export var LengthUnit = {
    Millimeter: 0,
    Centimeter: 1,
    Inch: 2,
    Feet: 3,
    Meter: 4,
    Kilometer: 5
};
/**
 * Returns true if the given object is a number. No type conversion will be performed.
 */
export function isNumber(pObj) {
    return (typeof pObj === "number" || pObj instanceof Number);
}
/**
 * Converts a localized string to a number. Number grouping is not supported (thousands separators).
 * Null will be returned if the conversion fails.
 */
export function fromLocaleString(pString) {
    if (pString == null)
        throw new Error(ERR_INVALID_ARGUMENT);
    var tStr = pString.trim();
    tStr = tStr.replace(",", ".");
    var tNumber = Number.parseFloat(tStr);
    if (tNumber == null || isNaN(tNumber))
        return (null);
    return (tNumber);
}
/**
 * Converts a number to a localized string.
 * Null will be returned if the conversion fails.
 */
export function toLocaleString(pValue, pLocale, pMinDecimalPlaces, pMaxDecimalplaces, pUseGrouping) {
    if (pMinDecimalPlaces === void 0) { pMinDecimalPlaces = 3; }
    if (pMaxDecimalplaces === void 0) { pMaxDecimalplaces = 3; }
    if (pUseGrouping === void 0) { pUseGrouping = false; }
    if (pValue == null || pLocale == null)
        throw new Error(ERR_INVALID_ARGUMENT);
    try {
        var tStr = pValue.toLocaleString(pLocale, {
            useGrouping: pUseGrouping,
            minimumFractionDigits: pMinDecimalPlaces,
            maximumFractionDigits: pMaxDecimalplaces
        });
        return (tStr);
    }
    catch (e) {
        return (null);
    }
}
/**
 * Converts a number to a localized currency string. Null will be returned if the conversion fails.
 */
export function toLocaleCurrencyString(pValue, pCurrency, pLocale, pOptions) {
    var _a;
    if (pValue == null || pCurrency == null || pLocale == null)
        throw new Error(ERR_INVALID_ARGUMENT);
    var tUseGrouping = true;
    var tShowCurrency = true;
    var tAdditionalDecimalPlaces = 0;
    if (pOptions != null) {
        tUseGrouping = pOptions.useGrouping === true;
        tShowCurrency = pOptions.showCurrency === true;
        tAdditionalDecimalPlaces = (_a = pOptions.additionalDecimalPlaces) !== null && _a !== void 0 ? _a : 0;
    }
    var tOptions = {
        useGrouping: tUseGrouping,
        currency: pCurrency,
        currencyDisplay: "code",
        style: "currency"
    };
    try {
        if (tShowCurrency && tAdditionalDecimalPlaces === 0) {
            return (pValue.toLocaleString(pLocale, tOptions));
        }
        else {
            //check browser support
            if (Intl != null && Intl.NumberFormat != null) {
                var tNumberFormat = new Intl.NumberFormat(pLocale, tOptions);
                var tResolvedOptions = tNumberFormat.resolvedOptions();
                tResolvedOptions.currency = undefined;
                tResolvedOptions.style = "decimal";
                tResolvedOptions.maximumFractionDigits += tAdditionalDecimalPlaces;
                return (pValue.toLocaleString(pLocale, tResolvedOptions));
            }
            else {
                //fallback
                return pValue.toLocaleString(pLocale, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2 + tAdditionalDecimalPlaces
                });
            }
        }
    }
    catch (e) {
        return (null);
    }
}
/**
 * Converts a localized length string to number. Number grouping is not supported (thousands separators).
 * If the string does not contain a length unit the default unit will be used.
 * The value will be returned in meter.
 * Null will be returned if the conversion fails.
 */
export function fromLocaleLengthString(pString, pDefaultUnit) {
    if (pDefaultUnit === void 0) { pDefaultUnit = LengthUnit.Meter; }
    if (pString == null)
        throw new Error(ERR_INVALID_ARGUMENT);
    //find last numeric char
    var tIndex = -1;
    for (var i = pString.length - 1; i >= 0; --i) {
        var tCharCode = pString.charCodeAt(i);
        if ((tCharCode >= 48 && tCharCode <= 57) || // "0" - "9"
            tCharCode === 46 || tCharCode === 44) // "." or ","
         {
            tIndex = i;
            break;
        }
    }
    if (tIndex < 0)
        return (null);
    var tNumStr = pString.substring(0, tIndex + 1);
    var tUnitStr = pString.substring(tIndex + 1).toLowerCase().trim();
    //detect length unit
    var tUnit = pDefaultUnit;
    switch (tUnitStr) {
        case ("mm"):
            tUnit = LengthUnit.Millimeter;
            break;
        case ("cm"):
            tUnit = LengthUnit.Centimeter;
            break;
        case ("in"):
        case ("''"):
        case ("\""):
            tUnit = LengthUnit.Inch;
            break;
        case ("ft"):
        case ("'"):
            tUnit = LengthUnit.Feet;
            break;
        case ("m"):
            tUnit = LengthUnit.Meter;
            break;
        case ("km"):
            tUnit = LengthUnit.Kilometer;
            break;
    }
    //convert string to number
    var tNum = fromLocaleString(tNumStr);
    if (tNum == null)
        return (null);
    //convert length unit
    return (convertLengthUnit(tNum, tUnit, LengthUnit.Meter));
}
/**
 * Converts a number to localized length string. The value is expected in meter.
 * Null will be returned if the conversion fails.
 */
export function toLocaleLengthString(pValue, pLocale, pTargetUnit, pAddUnit) {
    if (pTargetUnit === void 0) { pTargetUnit = LengthUnit.Meter; }
    if (pAddUnit === void 0) { pAddUnit = true; }
    if (pValue == null || pLocale == null)
        throw new Error(ERR_INVALID_ARGUMENT);
    //convert length unit
    var tValue = convertLengthUnit(pValue, LengthUnit.Meter, pTargetUnit);
    //get unit and decimla places
    var tUnitStr = "m";
    var tDecimals = 2;
    switch (pTargetUnit) {
        case (LengthUnit.Millimeter):
            tUnitStr = "mm";
            tDecimals = 0;
            break;
        case (LengthUnit.Centimeter):
            tUnitStr = "cm";
            tDecimals = 0;
            break;
        case (LengthUnit.Inch):
            tUnitStr = "\"";
            tDecimals = 1;
            break;
        case (LengthUnit.Feet):
            tUnitStr = "'";
            tDecimals = 2;
            break;
        case (LengthUnit.Meter):
            tUnitStr = "m";
            tDecimals = 2;
            break;
        case (LengthUnit.Kilometer):
            tUnitStr = "km";
            tDecimals = 5;
            break;
    }
    try {
        var tLengthStr = tValue.toLocaleString(pLocale, {
            useGrouping: false,
            minimumFractionDigits: tDecimals,
            maximumFractionDigits: tDecimals
        });
        if (tLengthStr != null && pAddUnit)
            tLengthStr += " " + tUnitStr;
        return (tLengthStr);
    }
    catch (e) {
        return (null);
    }
}
/**
 * Converts the given value into another length unit.
 */
export function convertLengthUnit(pValue, pFrom, pTo) {
    return (pValue * getLengthUnitValue(pFrom) / getLengthUnitValue(pTo));
}
function getLengthUnitValue(pUnit) {
    switch (pUnit) {
        case LengthUnit.Millimeter: return (10);
        case LengthUnit.Centimeter: return (100);
        case LengthUnit.Inch: return (254);
        case LengthUnit.Feet: return (3048);
        case LengthUnit.Meter: return 10000;
        case LengthUnit.Kilometer: return (10000000);
        default:
            return (0);
    }
}
